import { useMemo } from 'react';
import { getQuarterFromMonth, createQuarterlyData, round2 } from './useQuarterHelpers';

/**
 * Calcul de la quote-part de charges par consultant.
 * - Charges partagées (sans consultant) : divisées par le nombre de consultants
 * - Charges spécifiques (avec consultant) : montant entier attribué au consultant
 */
export function useQuotePartCalculations(charges, consultantConfig, filterAnnee, consultantNames) {
  return useMemo(() => {
    const configEntry = consultantConfig[filterAnnee];
    const nombreConsultants = typeof configEntry === 'object'
      ? (configEntry?.nombreConsultants || 0)
      : (configEntry || 0);

    // Accumuler les charges partagées par trimestre
    const sharedTotal = createQuarterlyData();
    // Accumuler les charges spécifiques par consultant
    const specificByConsultant = {};

    charges.forEach(charge => {
      const consultantNom = charge.consultantNom || null;

      // Déterminer dans quel trimestre cette charge tombe
      const addToQuarterly = (target) => {
        if (charge.type === 'Annuelle') {
          const montantParTrimestre = charge.montant / 4;
          target.Q1 += montantParTrimestre;
          target.Q2 += montantParTrimestre;
          target.Q3 += montantParTrimestre;
          target.Q4 += montantParTrimestre;
        } else if (charge.type === 'Trimestrielle') {
          if (charge.trimestre) target[charge.trimestre] += charge.montant;
        } else if (charge.type === 'Mensuelle' || charge.type === 'Exceptionnelle') {
          if (charge.mois) {
            const quarter = getQuarterFromMonth(charge.mois);
            target[quarter] += charge.montant;
          }
        }
        target.ANNUEL += charge.montant;
      };

      if (consultantNom) {
        // Charge spécifique à un consultant
        if (!specificByConsultant[consultantNom]) {
          specificByConsultant[consultantNom] = createQuarterlyData();
        }
        addToQuarterly(specificByConsultant[consultantNom]);
      } else {
        // Charge partagée
        addToQuarterly(sharedTotal);
      }
    });

    // Quote-part partagée par consultant
    const sharedQuotePart = createQuarterlyData();
    if (nombreConsultants > 0) {
      sharedQuotePart.Q1 = round2(sharedTotal.Q1 / nombreConsultants);
      sharedQuotePart.Q2 = round2(sharedTotal.Q2 / nombreConsultants);
      sharedQuotePart.Q3 = round2(sharedTotal.Q3 / nombreConsultants);
      sharedQuotePart.Q4 = round2(sharedTotal.Q4 / nombreConsultants);
      sharedQuotePart.ANNUEL = round2(sharedTotal.ANNUEL / nombreConsultants);
    }

    // Quote-part totale par consultant = partagée + spécifique
    const byConsultant = {};
    const names = consultantNames || [];
    names.forEach(name => {
      const specific = specificByConsultant[name] || createQuarterlyData();
      byConsultant[name] = {
        Q1: round2(sharedQuotePart.Q1 + specific.Q1),
        Q2: round2(sharedQuotePart.Q2 + specific.Q2),
        Q3: round2(sharedQuotePart.Q3 + specific.Q3),
        Q4: round2(sharedQuotePart.Q4 + specific.Q4),
        ANNUEL: round2(sharedQuotePart.ANNUEL + specific.ANNUEL)
      };
    });

    return {
      shared: sharedQuotePart,
      byConsultant
    };
  }, [charges, consultantConfig, filterAnnee, consultantNames]);
}
